import { cn, formatCount } from '@/lib/format'

export interface FilterChip<V extends string> {
  value: V
  label: string
  count?: number
}

export interface FilterChipsProps<V extends string> {
  /** Accessible name for the group, e.g. "Filter by status". */
  label: string
  options: FilterChip<V>[]
  value: V
  onChange: (value: V) => void
  className?: string
}

/** Single-select filter as a row of pressable chips with counts. Wraps on phones. */
export function FilterChips<V extends string>({ label, options, value, onChange, className }: FilterChipsProps<V>) {
  return (
    <div role="group" aria-label={label} className={cn('flex flex-wrap gap-1.5', className)}>
      {options.map((o) => {
        const active = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold transition-colors',
              active ? 'border-accent/60 bg-accent-soft text-accent-ink' : 'border-line bg-panel text-muted hover:border-line-strong hover:text-ink',
            )}
          >
            {o.label}
            {o.count !== undefined ? (
              <span className={cn('font-mono text-2xs tabular', active ? 'text-accent-ink/80' : 'text-subtle')}>{formatCount(o.count)}</span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
